import React, { useCallback, useEffect, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../redux/reducer";
import { clearCurrentEmployeeCache } from "../../utils/currentEmployee";
import { tokens } from "../../theme";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import BottomNav from "./BottomNav";
import { adminNavSections, operatorNavSections } from "./navConfig";

const COLLAPSE_KEY = "sup.sidebarCollapsed";

/**
 * Un solo layout para toda la app:
 *   < 768 px  → barra superior + barra inferior fija
 *   768–1279  → arranca como riel de íconos
 *   ≥ 1280 px → arranca con el menú lateral completo
 *
 * El ancho de pantalla sólo decide el estado INICIAL: en cualquier tamaño de
 * escritorio el usuario puede colapsar o expandir a mano, y su elección manda
 * (por ejemplo con el panel de una extensión comiéndose el ancho de la ventana).
 *
 * El scroll es el del documento y las barras van fijas/pegajosas: así no pelea
 * con las pantallas heredadas que traen sus propias cadenas de `height: 100%`.
 */
const AppShell = ({ isAdmin, children, sidebarBadges }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isRail = useMediaQuery(theme.breakpoints.between("md", "lg"));

  // `null` = todavía sin elección: el ancho de pantalla decide. Una vez que el
  // usuario toca el botón, su preferencia se guarda y pisa al breakpoint.
  const [userCollapsed, setUserCollapsed] = useState(() => {
    const saved = localStorage.getItem(COLLAPSE_KEY);
    if (saved === "true") return true;
    if (saved === "false") return false;
    return null;
  });

  useEffect(() => {
    if (userCollapsed === null) localStorage.removeItem(COLLAPSE_KEY);
    else localStorage.setItem(COLLAPSE_KEY, String(userCollapsed));
  }, [userCollapsed]);

  const collapsed = userCollapsed === null ? isRail : userCollapsed;
  const sidebarWidth = collapsed ? tokens.railWidth : tokens.sidebarWidth;

  const username =
    useSelector((state) => state.auth.username) || localStorage.getItem("username") || "";

  const handleLogout = useCallback(() => {
    localStorage.removeItem("signatureData");
    localStorage.removeItem("username");
    // Si no se limpia, el próximo usuario hereda el employeeId del anterior y
    // vería las órdenes ajenas.
    clearCurrentEmployeeCache();
    dispatch(logout());
    navigate("/login");
  }, [dispatch, navigate]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: tokens.canvas }}>
      {!isMobile && (
        <Box
          sx={{
            // Fijo: así llega siempre hasta abajo, sin importar cuánto scrollee la página.
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: sidebarWidth,
            flexShrink: 0,
            zIndex: 30,
          }}
        >
          <Sidebar
            collapsed={collapsed}
            canToggle
            onToggle={() => setUserCollapsed(!collapsed)}
            username={username}
            roleLabel={isAdmin ? "Administrador" : "Operador"}
            sections={isAdmin ? adminNavSections : operatorNavSections}
            onLogout={handleLogout}
            badges={isAdmin ? sidebarBadges || {} : {}}
          />
        </Box>
      )}

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          transition: "margin-left 160ms ease",
        }}
      >
        <Box sx={{ position: "sticky", top: 0, zIndex: 25 }}>
          <TopBar isMobile={isMobile} onLogout={handleLogout} />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            // Aire para que la barra inferior fija no tape la última fila.
            paddingBottom: isMobile
              ? `calc(${tokens.bottomNavHeight}px + env(safe-area-inset-bottom) + 12px)`
              : 0,
          }}
        >
          {children}
        </Box>
      </Box>

      {isMobile && <BottomNav isAdmin={isAdmin} onLogout={handleLogout} />}
    </Box>
  );
};

export default AppShell;
